import { collection, getDocs } from "firebase/firestore";
import { getFirebaseFirestore } from "@/config/firebase";
import { isDemoMode } from "@/demo/demoMode";
import * as demoStore from "@/demo/demoStore";
import type { Member } from "@vlab-corporation/vlab-pilates-service-interface/customer";
import type { MemberInstructorReRegistration } from "@vlab-corporation/vlab-pilates-service-interface/customer";

function membersCollection(ownerUid: string, centerId: number) {
  return collection(
    getFirebaseFirestore(),
    "users",
    ownerUid,
    "centers",
    String(centerId),
    "members",
  );
}

/**
 * 센터 회원 목록을 배열로 반환한다.
 * 동명이인이 있어도 안전하게 모든 회원을 반환한다.
 */
export async function getMembersList(ownerUid: string, centerId: number): Promise<Member[]> {
  if (isDemoMode()) return demoStore.getMembersList(centerId);

  const snap = await getDocs(membersCollection(ownerUid, centerId));
  return (
    snap.docs
      // 소프트 삭제된 회원(deleteMember가 removedAt 기록)은 목록에서 제외.
      // 백엔드 lookupMember도 removedAt 회원을 예약/구매 대상에서 제외하므로
      // (admin-web getAdminMembersList와 동일) 목록에 노출되면 예약 시 not-found가 난다.
      .filter((d) => (d.data() as { removedAt?: unknown }).removedAt == null)
      .map((d) => ({ id: d.id, ...d.data() }) as Member)
  );
}

/**
 * 센터 회원 목록을 이름 → Member 맵으로 반환한다.
 *
 * ⚠️ 동명이인 시 나중 데이터가 앞 데이터를 덮어쓴다.
 * 목록 표시에는 getMembersList()를 사용할 것.
 * 이 함수는 booking.memberName 기반 빠른 조회가 필요한 곳에서만 사용한다.
 */
export async function getMembers(ownerUid: string, centerId: number): Promise<Map<string, Member>> {
  if (isDemoMode()) return demoStore.getMembers(centerId);

  const snap = await getDocs(membersCollection(ownerUid, centerId));
  const map = new Map<string, Member>();
  for (const d of snap.docs) {
    const member = { id: d.id, ...d.data() } as Member;
    // ID 키: sync 후 studiomateMemberId 기반 조회 (이름 불일치 방지)
    map.set(member.id, member);
    // 이름 키: 캘린더 API가 member.id를 반환하지 않을 때 fallback
    map.set(member.name, member);
  }
  return map;
}

function reRegistrationsCollection(ownerUid: string, centerId: number) {
  return collection(
    getFirebaseFirestore(),
    "users",
    ownerUid,
    "centers",
    String(centerId),
    "reRegistrations",
  );
}

/**
 * 센터의 전체 재등록 관계를 Map으로 반환한다.
 * key: "${memberId}_${instructorId}", value: reRegistrationDate
 */
export async function getReRegistrations(
  ownerUid: string,
  centerId: number,
): Promise<Map<string, string>> {
  if (isDemoMode()) return new Map();

  const snap = await getDocs(reRegistrationsCollection(ownerUid, centerId));
  const map = new Map<string, string>();
  for (const d of snap.docs) {
    const data = d.data() as MemberInstructorReRegistration;
    if (data.memberId && data.instructorId && data.reRegistrationDate) {
      map.set(`${data.memberId}_${data.instructorId}`, data.reRegistrationDate);
    }
  }
  return map;
}
